export const ALLOWED_IMAGE_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
]);

const TRANSCODABLE_IMAGE_TYPES = new Set([
  ...ALLOWED_IMAGE_TYPES,
  "image/heic",
  "image/heif",
]);

export const DEFAULT_MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const DEFAULT_TARGET_IMAGE_BYTES = 500 * 1024;
export const DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES = 1024 * 1024;
export const DEFAULT_FULL_IMAGE_LONG_EDGE = 1600;
export const DEFAULT_THUMBNAIL_LONG_EDGE = 400;
export const DEFAULT_SKIP_COMPRESSION_BYTES = 300 * 1024;
export const DEFAULT_SKIP_COMPRESSION_LONG_EDGE = 1200;
export const DEFAULT_MIN_COMPRESSION_QUALITY = 0.46;
export const DEFAULT_MIN_COMPRESSION_LONG_EDGE = 700;

const EXTENSION_BY_TYPE = {
  "image/jpeg": "jpg",
  "image/png": "png",
  "image/webp": "webp",
};

const hasKnownImageExtension = (filename = "") =>
  /\.(jpe?g|png|webp|heic|heif)$/i.test(String(filename || ""));

const isManagedImageInputTypeAllowed = (file) => {
  const contentType = String(file?.type || "").toLowerCase();
  if (TRANSCODABLE_IMAGE_TYPES.has(contentType)) return true;
  if (!contentType && hasKnownImageExtension(file?.name || "")) return true;
  return false;
};

const supportsBrowserImageOptimization = () =>
  typeof document !== "undefined" &&
  typeof Blob !== "undefined" &&
  typeof File !== "undefined";

export const normalizeManagedImageFilename = (value = "image") => {
  const raw = String(value || "image").trim() || "image";
  const withoutPath = raw.split(/[\\/]/).pop() || "image";
  const cleaned = withoutPath
    .normalize("NFKD")
    .replace(/[^\w.-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^\.+/, "")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();
  return (cleaned || "image").slice(0, 90);
};

export const replaceImageFileExtension = (filename = "image", extension = "jpg") => {
  const safe = normalizeManagedImageFilename(filename);
  const base = safe.replace(/\.[a-z0-9]{2,5}$/i, "") || "image";
  return `${base}.${extension}`;
};

export const validateManagedImageFile = (
  file,
  { maxBytes = DEFAULT_MAX_IMAGE_BYTES } = {}
) => {
  if (!file) {
    return { valid: false, message: "Please choose an image." };
  }
  if (!isManagedImageInputTypeAllowed(file)) {
    return { valid: false, message: "Please choose a JPG, PNG, WebP, HEIC, or HEIF image." };
  }
  if (Number(file.size || 0) > maxBytes) {
    const maxMb = Math.max(1, Math.floor(maxBytes / (1024 * 1024)));
    return { valid: false, message: `Image is too large. Please choose an image under ${maxMb} MB.` };
  }
  return { valid: true, message: "" };
};

const createCanvas = (width, height) => {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  return canvas;
};

const loadImageElement = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(image);
    };
    image.onerror = (error) => {
      URL.revokeObjectURL(url);
      reject(error);
    };
    image.src = url;
  });

const loadBitmap = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch (error) {
      // Fall back to HTMLImageElement for browsers without orientation support.
    }
  }
  return loadImageElement(file);
};

const getBitmapDimensions = (bitmap) => ({
  width: Number(bitmap?.width || bitmap?.naturalWidth || 0) || 1,
  height: Number(bitmap?.height || bitmap?.naturalHeight || 0) || 1,
});

const canvasToBlob = (canvas, contentType, quality) =>
  new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), contentType, quality);
  });

const drawToCanvas = (bitmap, maxLongEdge) => {
  const { width: originalWidth, height: originalHeight } = getBitmapDimensions(bitmap);
  const longEdge = Math.max(originalWidth, originalHeight);
  const scale = longEdge > maxLongEdge ? maxLongEdge / longEdge : 1;
  const width = Math.max(1, Math.round(originalWidth * scale));
  const height = Math.max(1, Math.round(originalHeight * scale));
  const canvas = createCanvas(width, height);
  const context = canvas.getContext("2d", { alpha: true });
  context.drawImage(bitmap, 0, 0, width, height);
  return { canvas, width, height, originalWidth, originalHeight };
};

const uniqueDescendingNumbers = (values = []) =>
  Array.from(
    new Set(
      values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0)
        .map((value) => Math.round(value))
    )
  ).sort((a, b) => b - a);

const uniqueDescendingQualities = (values = []) =>
  Array.from(
    new Set(
      values
        .map((value) => Number(value))
        .filter((value) => Number.isFinite(value) && value > 0 && value <= 1)
        .map((value) => Math.round(value * 100) / 100)
    )
  ).sort((a, b) => b - a);

const canvasHasTransparency = (canvas) => {
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) return false;
  const { width, height } = canvas;
  const stepX = Math.max(1, Math.floor(width / 80));
  const stepY = Math.max(1, Math.floor(height / 80));
  try {
    const imageData = context.getImageData(0, 0, width, height).data;
    for (let y = 0; y < height; y += stepY) {
      for (let x = 0; x < width; x += stepX) {
        const alpha = imageData[(y * width + x) * 4 + 3];
        if (alpha < 255) return true;
      }
    }
  } catch (error) {
    return false;
  }
  return false;
};

const makeImageFile = (blob, sourceFile, contentType, suffix = "") => {
  const extension = EXTENSION_BY_TYPE[contentType] || "jpg";
  const filename = replaceImageFileExtension(
    suffix
      ? `${normalizeManagedImageFilename(sourceFile.name).replace(/\.[a-z0-9]{2,5}$/i, "")}-${suffix}`
      : sourceFile.name,
    extension
  );
  return new File([blob], filename, {
    type: contentType,
    lastModified: Date.now(),
  });
};

const buildFallbackResult = async (file, status = "fallback-original") => {
  let originalWidth = 0;
  let originalHeight = 0;
  if (supportsBrowserImageOptimization()) {
    try {
      const bitmap = await loadBitmap(file);
      const dimensions = getBitmapDimensions(bitmap);
      originalWidth = dimensions.width;
      originalHeight = dimensions.height;
      if (typeof bitmap.close === "function") bitmap.close();
    } catch (error) {
      // Metadata is optional; upload should still continue.
    }
  }
  const contentType = String(file.type || "image/jpeg").toLowerCase();
  return {
    optimizedFile: file,
    file,
    originalSizeBytes: file.size,
    optimizedSizeBytes: file.size,
    compressionRatio: 1,
    width: originalWidth,
    height: originalHeight,
    originalWidth,
    originalHeight,
    contentType,
    extension: EXTENSION_BY_TYPE[contentType] || "jpg",
    compressionStatus: status,
    transparencyDetected: false,
  };
};

export const optimizeImageFile = async ({
  file,
  maxLongEdge = DEFAULT_FULL_IMAGE_LONG_EDGE,
  quality = 0.85,
  outputFormat = "image/webp",
  preserveTransparency = true,
  skipCompressionBytes = DEFAULT_SKIP_COMPRESSION_BYTES,
  skipCompressionLongEdge = DEFAULT_SKIP_COMPRESSION_LONG_EDGE,
  targetBytes = DEFAULT_TARGET_IMAGE_BYTES,
  hardMaxBytes = DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES,
  minQuality = DEFAULT_MIN_COMPRESSION_QUALITY,
  minLongEdge = DEFAULT_MIN_COMPRESSION_LONG_EDGE,
} = {}) => {
  const validation = validateManagedImageFile(file);
  if (!validation.valid) {
    const error = new Error(validation.message);
    error.code = "invalid-image-file";
    throw error;
  }
  if (!supportsBrowserImageOptimization()) {
    return buildFallbackResult(file, "fallback-original");
  }

  try {
    const bitmap = await loadBitmap(file);
    const firstDrawn = drawToCanvas(bitmap, maxLongEdge);
    const longEdge = Math.max(firstDrawn.originalWidth, firstDrawn.originalHeight);
    const hasTransparency =
      preserveTransparency &&
      String(file.type || "").toLowerCase() === "image/png" &&
      canvasHasTransparency(firstDrawn.canvas);
    const sourceContentType = String(file.type || "").toLowerCase();
    const sourceIsUploadReady = ALLOWED_IMAGE_TYPES.has(sourceContentType);
    const contentType = hasTransparency ? "image/png" : outputFormat;
    const safeTargetBytes = Math.max(
      120 * 1024,
      Number(targetBytes || DEFAULT_TARGET_IMAGE_BYTES)
    );
    const safeHardMaxBytes = Math.max(
      safeTargetBytes,
      Number(hardMaxBytes || DEFAULT_HARD_MAX_OPTIMIZED_IMAGE_BYTES)
    );
    const qualitySteps =
      contentType === "image/png"
        ? [1]
        : uniqueDescendingQualities([
            quality,
            0.82,
            0.76,
            0.7,
            0.64,
            0.58,
            0.52,
            minQuality,
          ]);
    const longEdgeSteps = uniqueDescendingNumbers([
      Math.min(maxLongEdge, longEdge),
      1400,
      1200,
      1000,
      850,
      minLongEdge,
    ]).filter(
      (edge) =>
        edge <= Math.max(maxLongEdge, longEdge) &&
        edge >= Math.min(minLongEdge, maxLongEdge)
    );
    const shouldSkipSmall =
      sourceIsUploadReady &&
      file.size <= Math.min(skipCompressionBytes, safeTargetBytes) &&
      longEdge <= skipCompressionLongEdge;
    if (shouldSkipSmall) {
      if (typeof bitmap.close === "function") bitmap.close();
      const fallback = await buildFallbackResult(file, "skipped-already-under-target");
      return {
        ...fallback,
        originalWidth: firstDrawn.originalWidth,
        originalHeight: firstDrawn.originalHeight,
        width: firstDrawn.originalWidth,
        height: firstDrawn.originalHeight,
        transparencyDetected: hasTransparency,
      };
    }

    let targetCandidate = null;
    let hardLimitCandidate = null;
    let smallestCandidate = null;

    const considerCandidate = (candidate) => {
      if (!candidate) return false;
      if (!smallestCandidate || candidate.optimizedFile.size < smallestCandidate.optimizedFile.size) {
        smallestCandidate = candidate;
      }
      if (
        candidate.optimizedFile.size <= safeHardMaxBytes &&
        (!hardLimitCandidate ||
          candidate.optimizedFile.size < hardLimitCandidate.optimizedFile.size)
      ) {
        hardLimitCandidate = candidate;
      }
      if (candidate.optimizedFile.size <= safeTargetBytes) {
        targetCandidate = candidate;
        return true;
      }
      return false;
    };

    const renderCandidate = async (edge, candidateQuality, targetContentType = contentType) => {
      const drawn =
        edge === Math.min(maxLongEdge, longEdge) ? firstDrawn : drawToCanvas(bitmap, edge);
      const blob = await canvasToBlob(drawn.canvas, targetContentType, candidateQuality);
      if (!blob) return null;
      const optimizedFile = makeImageFile(blob, file, targetContentType);
      return {
        optimizedFile,
        file: optimizedFile,
        originalSizeBytes: file.size,
        optimizedSizeBytes: optimizedFile.size,
        compressionRatio: file.size ? optimizedFile.size / file.size : 1,
        width: drawn.width,
        height: drawn.height,
        originalWidth: drawn.originalWidth,
        originalHeight: drawn.originalHeight,
        contentType: targetContentType,
        extension: EXTENSION_BY_TYPE[targetContentType] || "jpg",
        compressionQuality: candidateQuality,
        compressionLongEdge: edge,
        transparencyDetected: hasTransparency,
      };
    };

    const outputContentTypes = hasTransparency
      ? ["image/png"]
      : Array.from(new Set([contentType, "image/jpeg"]));

    for (const targetContentType of outputContentTypes) {
      for (const edge of longEdgeSteps) {
        const targetQualitySteps =
          targetContentType === "image/png" ? [1] : qualitySteps;
        for (const candidateQuality of targetQualitySteps) {
          const hitTarget = considerCandidate(
            await renderCandidate(edge, candidateQuality, targetContentType)
          );
          if (hitTarget) break;
        }
        if (targetCandidate) break;
      }
      if (targetCandidate) break;
    }

    if (typeof bitmap.close === "function") bitmap.close();

    const selectedCandidate = targetCandidate || hardLimitCandidate || smallestCandidate;
    if (!selectedCandidate) return buildFallbackResult(file, "fallback-original");

    if (
      sourceIsUploadReady &&
      file.size <= safeTargetBytes &&
      selectedCandidate.optimizedFile.size > file.size * 0.9
    ) {
      const fallback = await buildFallbackResult(file, "skipped-already-under-target");
      return {
        ...fallback,
        originalWidth: firstDrawn.originalWidth,
        originalHeight: firstDrawn.originalHeight,
        width: firstDrawn.originalWidth,
        height: firstDrawn.originalHeight,
        transparencyDetected: hasTransparency,
      };
    }

    if (
      sourceIsUploadReady &&
      file.size <= safeHardMaxBytes &&
      selectedCandidate.optimizedFile.size > file.size
    ) {
      const fallback = await buildFallbackResult(file, "skipped-original-smaller-under-1mb");
      return {
        ...fallback,
        originalWidth: firstDrawn.originalWidth,
        originalHeight: firstDrawn.originalHeight,
        width: firstDrawn.originalWidth,
        height: firstDrawn.originalHeight,
        transparencyDetected: hasTransparency,
      };
    }

    return {
      ...selectedCandidate,
      compressionStatus: targetCandidate
        ? "optimized-target-500kb"
        : hardLimitCandidate
        ? "optimized-under-1mb"
        : "optimized-best-effort",
    };
  } catch (error) {
    return buildFallbackResult(file, "fallback-original");
  }
};

export const createThumbnailFile = async ({
  file,
  maxLongEdge = DEFAULT_THUMBNAIL_LONG_EDGE,
  quality = 0.78,
  preserveTransparency = true,
} = {}) => {
  const validation = validateManagedImageFile(file);
  if (!validation.valid) {
    const error = new Error(validation.message);
    error.code = "invalid-image-file";
    throw error;
  }
  if (!supportsBrowserImageOptimization()) {
    return { thumbnailFile: null, thumbnailStatus: "fallback-unavailable" };
  }

  try {
    const bitmap = await loadBitmap(file);
    const drawn = drawToCanvas(bitmap, maxLongEdge);
    const hasTransparency =
      preserveTransparency &&
      String(file.type || "").toLowerCase() === "image/png" &&
      canvasHasTransparency(drawn.canvas);
    const contentType = hasTransparency ? "image/png" : "image/webp";
    const blob = await canvasToBlob(drawn.canvas, contentType, quality);
    if (typeof bitmap.close === "function") bitmap.close();
    if (!blob) return { thumbnailFile: null, thumbnailStatus: "failed" };
    const thumbnailFile = makeImageFile(blob, file, contentType, "thumb");
    return {
      thumbnailFile,
      thumbnailStatus: "verified",
      thumbnailSizeBytes: thumbnailFile.size,
      thumbnailWidth: drawn.width,
      thumbnailHeight: drawn.height,
      thumbnailContentType: contentType,
      thumbnailExtension: EXTENSION_BY_TYPE[contentType] || "webp",
    };
  } catch (error) {
    return { thumbnailFile: null, thumbnailStatus: "failed" };
  }
};
